import { useState, useEffect } from 'react';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import client from '../../../client';
import { useMessage } from 'app/shared-components/MessageUtils';
import { useTranslation } from 'react-i18next';
import ModalAdjustmentTransactionMessage from './ModalAdjustmentTransactionMessage';

const ModalAdjustmentTransaction = ({ open, setOpen, containerTankId, tankNickname, tankData, onAdded }) => {
  const { t } = useTranslation('navigation');
  const showMessageUtil = useMessage();
  const [form, setForm] = useState({
    compacctnum: '',
    container_tank_id: containerTankId,
    quantity: '',
    tankData: tankData || {},
  });
  const [openConfirmationModal, setOpenConfirmationModal] = useState(false);
  const [validationResponse, setValidationResponse] = useState(null);
  const [checkNoPendingTransactions, setCheckNoPendingTransactions] = useState(false);
  const [checkCurrentInventory, setCheckCurrentInventory] = useState(false);

  useEffect(() => {
    // Obtener compacctnum de localStorage y actualizar tankData
    try {
      const lSData = JSON.parse(localStorage.getItem('persist:root'));
      const userData = JSON.parse(lSData.loadz_auth).AuthReducer.user;
      setForm(f => ({ 
        ...f, 
        compacctnum: userData.company.compacctnum,
        container_tank_id: containerTankId,
        tankData: tankData || {}
      }));
    } catch (e) {}
  }, [open, containerTankId, tankData]);

  const handleChange = e => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleClose = () => {
    setOpen(false);
    // Reiniciar los checkboxes al cerrar el modal
    setCheckNoPendingTransactions(false);
    setCheckCurrentInventory(false);
  };

  const handleSubmit = async () => {
    try {
      console.log('Sending adjustment transaction data:', form);
      const response = await client.post('/fuel-inventory/validate-adjustment-transaction/', { ...form });
      
      // Guardar la respuesta de validación
      setValidationResponse(response.data);
      
      // Cerrar el modal actual
      setOpen(false);
      
      // Abrir el modal de confirmación
      setOpenConfirmationModal(true);
    } catch (err) {
      let errorMessage = t('ERROR_VALIDATING_ADJUSTMENT_TRANSACTION') || 'Error validating adjustment transaction';
      
      if (err.response && err.response.data) {
        if (err.response.data.message) {
          errorMessage = err.response.data.message;
        } else if (err.response.data.error) {
          errorMessage = err.response.data.error;
        } else if (typeof err.response.data === 'string') {
          errorMessage = err.response.data;
        }
      } else if (err.message) {
        errorMessage = err.message;
      }
      
      showMessageUtil(errorMessage, 'error');
    }
  };

  const handleConfirmationSuccess = () => {
    // Cerrar el modal de confirmación
    setOpenConfirmationModal(false);
    
    // Limpiar el formulario
    setForm({
      compacctnum: '',
      container_tank_id: containerTankId,
      quantity: '',
      tankData: tankData || {},
    });
    
    // Reiniciar los checkboxes
    setCheckNoPendingTransactions(false);
    setCheckCurrentInventory(false);
    
    // Llamar a onAdded para refrescar los datos
    if (onAdded) onAdded();
  };

  return (
    <>
      <Dialog 
        open={open} 
        onClose={handleClose}
        PaperProps={{
          sx: {
            width: '600px',
            maxWidth: '600px'
          }
        }}
      >
        <DialogTitle>{t('ADJUSTMENT_TRANSACTION') || 'Adjustment Transaction'}</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <label>{t('TANK_NICKNAME') || 'Tank Nickname'}</label>
                <TextField
                  margin="normal"
                  fullWidth
                  name="tankNickname"
                  value={tankNickname || ''}
                  disabled
                  inputProps={{ style: { padding: '5px' } }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <label>{t('INVENTORY_MEASURE') || 'Inventory Measure'}</label>
                <TextField
                  margin="normal"
                  fullWidth
                  name="quantity"
                  type="number"
                  value={form.quantity}
                  onChange={handleChange}
                  inputProps={{ style: { padding: '5px' } }}
                  placeholder={t('ENTER_INVENTORY_MEASURE') || 'Enter inventory measure'}
                />
              </Grid>
            </Grid>
            
            <Box sx={{ mt: 3 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={checkNoPendingTransactions}
                    onChange={(e) => setCheckNoPendingTransactions(e.target.checked)}
                    color="primary"
                  />
                }
                label="By making this adjustment to the inventory, I understand that there are no pending transactions to capture."
              />
            </Box>
            
            <Box sx={{ mt: 2 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={checkCurrentInventory}
                    onChange={(e) => setCheckCurrentInventory(e.target.checked)}
                    color="primary"
                  />
                }
                label="I understand that what I am entering in this form is the current quantity of my inventory."
              />
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>{t('CANCEL') || 'Cancel'}</Button>
          <Button 
            onClick={handleSubmit} 
            variant="contained" 
            color="primary"
            disabled={!checkNoPendingTransactions || !checkCurrentInventory}
          >
            {t('SUBMIT') || 'Submit'}
          </Button>
        </DialogActions>
      </Dialog>
      
      <ModalAdjustmentTransactionMessage 
        open={openConfirmationModal}
        setOpen={setOpenConfirmationModal}
        validationResponse={validationResponse}
        formData={form}
        onSuccess={handleConfirmationSuccess}
      />
    </>
  );
};

export default ModalAdjustmentTransaction;

